/**
 * ==========================================
 * BANANA FLOW CUSTOM CART DRAWER OVERRIDE
 * Based on Symmetry 8.0.0
 * Modified March 2026
 * ==========================================
 */

/* global SideDrawer */

class CartDrawer extends SideDrawer {

  connectedCallback() {
    
    this.bindEvents();
    this.bindToggleButtons();

    this.cartData = null;
    this.cartFetchPromise = null;

    this.originalCartRefreshHandler = this.cartRefreshHandler.bind(this);
    this.cartRefreshHandler = () => {
      this.originalCartRefreshHandler();
      this.updateCartItems(false);
    };

    this.updateRentButtonAvailability = this.updateRentButtonAvailability.bind(this);
    this.updateCartItems(false);
    this.bindGlobalRentSlider();

    this.addEventListener('on:cart:after-merge', () => {
      this.updateCartItems(true);
    });

    this.invalidateCartCache = () => {
      this.cartData = null;
    };

    document.addEventListener('on:cart:after-merge', this.invalidateCartCache);

    this.addEventListener('change', (e) => {
      const cartDrawer = this.querySelector('.cart-drawer');
      const cartMode = cartDrawer?.dataset.mode;

      if (cartMode === 'rent' && (
        e.target.name === 'updates[]' || 
        e.target.classList.contains('cart-quantity-input')
      )) {
        this.updateCartItems(false);
      }
    });

    document.addEventListener('on:cart:after-merge', this.updateRentButtonAvailability);

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.request-button button');
      if (!btn) return;
      this.openRequestModal(this.currentTotalRent || 0, this.currentAdditionalFee || 0);
    });

    document.addEventListener('DOMContentLoaded', async () => {
      const params = new URLSearchParams(window.location.search);

      if (params.get('contact_posted') === 'true') {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          await drawer.handleRentSuccess();
        }
      }
    });
  }

  async handleRentSuccess() {
    const cart = await this.refreshCartData();
    if (!cart) return;

    const updates = {};

    cart.items.forEach(item => {
      if (
        item.product_type === 'Griffe' ||
        item.product_type === 'Macros' ||
        item.product_type === 'Volumen'
      ) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length > 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
    }

    this.cartData = null;

    window.history.replaceState({}, document.title, window.location.pathname);

    window.location.href = '/?rent_success=true';
  }


  async refreshCartData() {

    if (this.cartFetchPromise) return this.cartFetchPromise;

    this.cartFetchPromise = fetch('/cart.js')
      .then(res => res.json())
      .then(data => {
        this.cartData = data;
        this.cartFetchPromise = null;
        return data;
      })
      .catch(err => {
        this.cartFetchPromise = null;
        console.error(err);
      });

    return this.cartFetchPromise;
  }

  async getCart() {
    if (this.cartData) return this.cartData;
    return this.refreshCartData();
  }


  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.removeEventListener('on:cart:after-merge', this.updateRentButtonAvailability);
    document.removeEventListener('on:cart:after-merge', this.invalidateCartCache);
  }

  bindToggleButtons() {
    const buyBtn = this.querySelector('#cartBuyToggle');
    const rentBtn = this.querySelector('#cartRentToggle');
    const cartContainer = this.querySelector('.cart-drawer') || document.querySelector('.cart-drawer');

    if (!buyBtn || !rentBtn) return;

    buyBtn.addEventListener('click', () => {
      buyBtn.classList.add('active');
      rentBtn.classList.remove('active');
      cartContainer.dataset.mode = 'buy';
      this.updateCartItems(false);
    });
  
    rentBtn.addEventListener('click', () => {  
      rentBtn.classList.add('active');
      buyBtn.classList.remove('active');
      cartContainer.dataset.mode = 'rent';
      this.updateCartItems(false);
      });
       
    this.bindGlobalRentSlider();
    this.updateCartItems(false);
  }


    bindGlobalRentSlider() {
    const slider = this.querySelector('#globalRentSlider');
    if (!slider) return;
    if (slider.dataset.bound === 'true') return;
    slider.dataset.bound = 'true';
    const durationText = this.querySelector('.rent-duration-value');
    const totalText = this.querySelector('.rent-total-price');
    const cartItems = this.querySelectorAll('.cart-item-list__body > div[data-merge-list-item]');

    const allowedValues = [12, 24, 48];

    slider.addEventListener('input', (e) => {
      const selectedIndex = parseInt(e.target.value); 
      const months = allowedValues[selectedIndex]; 

      durationText.textContent = `${months} Monat${months > 1 ? 'e' : ''}`;

      this.updateCartItems(false);
    });
  }


  async updateCartItems(refreshNeeded = false ) {

    const cartDrawer = this.querySelector('.cart-drawer');
    let cartMode = cartDrawer?.dataset.mode || 'buy';

    const rentSubtotal = this.querySelector('.cart-rent-subtotal');
    const buySubtotal = this.querySelector('.cart-buy-subtotal');
    const rentControls = this.querySelector('.cart-rent-controls');
    const rentPriceEl = this.querySelector('.rent-monthly-price');
    const checkoutButton = this.querySelector('.checkout-buttons');
    const requestButton = this.querySelector('.request-button');
    const crossSell = this.querySelector('.cross-sell');
    const discounts = this.querySelectorAll('.cart-item__item-discounts');
    const additionalFee = this.querySelector('.rent-additional-fee');
    const additionalPrice = this.querySelector('.rent-additional-price');
    const taxInfo = this.querySelector('.cart-policies.rte');
    const delivery = this.querySelector('.toggle-target-toggler--is-hidden.link.underline');
    const cartModeToggle = this.querySelector('.cart-mode-toggle');
    const flowInfo = this.querySelector('.product-flow-info.in-cart');

    let cart = await this.getCart();

    if (refreshNeeded === true) {
      cart = await this.refreshCartData();
    }
    
    if (!cart) return;


    const hasRentItems = cart.items.some(item =>
      item.product_type === 'Griffe' ||
      item.product_type === 'Volumen'
    );



    if (!hasRentItems) {
      // hide toggle
      if (cartModeToggle) {
        cartModeToggle.style.display = 'none';
      }
      if (cartMode === 'rent') {
        cartMode = 'buy';
      }; 
    } else {
      if (cartModeToggle) {
        cartModeToggle.style.display = '';
      }
    }

    const items = this.querySelectorAll('[data-merge-list-item]');
    const processedKeys = new Set();

    if (cartMode === 'rent') {
      buySubtotal?.style && (buySubtotal.style.display = 'none');
      rentSubtotal?.style && (rentSubtotal.style.display = 'flex');
      checkoutButton?.style && (checkoutButton.style.display = 'none');
      requestButton?.style && (requestButton.style.display = 'flex');
      crossSell?.style && (crossSell.style.display = 'none');    
      discounts?.forEach(el => {
        el.style.setProperty('display', 'none', 'important');
      });
      additionalFee?.style && (additionalFee.style.display = '');
      delivery?.style && (delivery.style.display = 'none');
      taxInfo?.style && (taxInfo.style.display = 'none');
      flowInfo?.style && (flowInfo.style.display = 'flex');

      const months = parseInt(this.querySelector('#globalRentSlider')?.value || 2);

      let totalRent = 0;
      let additional = 0;

      for (const domItem of items) {
        
        const rawKey = domItem.dataset.mergeListItem;
        if (!rawKey) continue;

        const key = rawKey.replace('item-key:', '').split('|')[0];

        if (processedKeys.has(key)) {
          domItem.style.display = 'none';
          continue;
        }
        processedKeys.add(key);

        const cartItem = cart.items.find(i => i.key === key);
        if (!cartItem) {
          domItem.style.display = 'none';
          continue;
        }

        const type = cartItem.product_type;
        const quantity = cartItem.quantity;
        const base = await this.getVariantComparePrice(cartItem);
        if (type === 'Griffe' || type === 'Volumen') {
          totalRent += this.calculateRentPrice(base, months, quantity, type);
          additional += base * quantity;
          domItem.style.display = '';
        } else {
          domItem.style.display = 'none';
        }
      }

      additional = additional * 0.1 / 100; 

      if (rentPriceEl) {
        rentPriceEl.textContent = this.formatMoney(totalRent);      
        this.currentTotalRent = totalRent;
      } 
      if (additionalPrice) {
        additionalPrice.textContent = this.formatMoney(additional);
        this.currentAdditionalFee = additional;
    }
         

    } else {
      buySubtotal?.style && (buySubtotal.style.display = 'flex');
      rentSubtotal?.style && (rentSubtotal.style.display = 'none');
      crossSell?.style && (crossSell.style.display = 'flex');
      discounts?.forEach(el => {
        el.style.setProperty('display', '');
      });
      additionalFee?.style && (additionalFee.style.display = 'none');
      delivery?.style && (delivery.style.display = '');
      taxInfo?.style && (taxInfo.style.display = '');
      checkoutButton?.style && (checkoutButton.style.display = '');
      requestButton?.style && (requestButton.style.display = 'none');
      flowInfo?.style && (flowInfo.style.display = 'none');

      for (const domItem of items) {
        domItem.style.display = '';
      }

    }

    if (rentControls?.style) {
      rentControls.style.display = cartMode === 'rent' ? 'block' : 'none';
    }

    this.bindGlobalRentSlider(); 
    this.updateRentButtonAvailability();
  }

  
  async getVariantComparePrice(cartItem) {
    this.productCache = this.productCache || {};

    if (!this.productCache[cartItem.handle]) {
      const res = await fetch(`/products/${cartItem.handle}.js`);
      this.productCache[cartItem.handle] = await res.json();
    }

    const product = this.productCache[cartItem.handle];
    const variant = product.variants.find(v => v.id === cartItem.variant_id);
    return variant.compare_at_price || variant.price;
  }


  calculateRentPrice(basePrice, months, quantity=1, type='Griffe') {
    let price = 0;

    // List of Rent-Prices
    //       Monate: 12, 24, 48
    const prices = [349, 179, 99, // Griffe/Macros A
                    279, 139, 69, // Griffe/Macros B
                    179, 99, 59,  // Griffe/Macros C
                    239, 139, 69, // Volumen A
                    179, 99, 59]; // Volumen B
    if (type === 'Griffe') {
      if (basePrice > 410000) { // -> A
        price = prices[months];
      } else if 
      (basePrice <= 410000 && 
      basePrice > 330000) {     // -> B
        price = prices[months + 3];
      } else {                  // -> C
        price = prices[months + 6];
      };
    } else if (type === 'Volumen') {
      if (basePrice > 330000) { // -> A
        price = prices[months + 9];
      } else {                  // -> B
        price = prices[months + 12];
      };
    }
    
    return price * quantity;  
  }

  formatMoney(amount) {
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return `€${formatted}`;
  }



  async updateRentButtonAvailability() {

    const minRentTotal = 250000; // €2.500,00 in cents; minimum Amount for Rent-Option to be available
    const rentToggle = document.getElementById('cartRentToggle');
    const requestWrapper = this.querySelector('.request-button');
    const requestBtn = requestWrapper?.querySelector('button');

    if (!rentToggle || !requestWrapper || !requestBtn) return;

    const cart = await this.getCart();
    if (!cart) return;

    const eligible = cart.total_price >= minRentTotal;

    rentToggle.disabled = !eligible;
    requestBtn.disabled = !eligible;
    requestBtn.classList.toggle('is-disabled', !eligible);

    this.renderRentEligibilityNote(requestWrapper, eligible, minRentTotal);
  }

  renderRentEligibilityNote(wrapper, eligible, minAmount) {
    let note = wrapper.querySelector('.rent-eligibility-note');

    if (eligible) {
      if (note) note.remove();
      return;
    }

    if (!note) {
      note = document.createElement('div');
      note.className = 'rent-eligibility-note';
      wrapper.appendChild(note);
    }

    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(minAmount / 100);

    note.textContent = `Anfrage möglich ab einem Warenwert von ${formatted}`;
  }


  async openRequestModal(totalRent = 0, additionalFee = 0) {
    if (document.querySelector('#rentRequestModal')) return;

    const cart = await this.getCart();
    if (!cart) return;

    const slider = this.querySelector('#globalRentSlider'); 
    const allowedValues = [12, 24, 48]; 
    const durationIndex = slider ? parseInt(slider.value, 10) : 0; 
    const rentDuration = allowedValues[durationIndex] || 12; 

    const itemsContainer = this.querySelectorAll('[data-merge-list-item]'); 
    const groupedItems = {}; 

    Array.from(itemsContainer).forEach(el => { 
      const rawKey = el.dataset.mergeListItem; 
      if (!rawKey) return; 

      const key = rawKey.replace('item-key:', '').split('|')[0]; 
      const cartItem = cart.items.find(i => i.key === key); 
      if (!cartItem) return; 

      const type = cartItem.product_type; 
      if (type !== 'Griffe' && type !== 'Volumen') return; 

      const title = cartItem.product_title; 
      const qty = cartItem.quantity; 

      const color = cartItem.properties?.Color || null; 

      if (!groupedItems[title]) groupedItems[title] = { qty: 0, color, image: cartItem.image }; 

      groupedItems[title].qty += qty; 
    }); 

    let itemsText = 'Produkte:\n'; 
    Object.entries(groupedItems).forEach(([title, data]) => { 
      itemsText += `${title}\n`;
      if (data.color) itemsText += `  Farbe: ${data.color}\n`;
      itemsText += `  Menge: ${data.qty}\n\n`;
    }); 

    const modal = document.createElement('div'); 
    modal.id = 'rentRequestModal'; 
    modal.innerHTML = ` 
      <div class="rent-modal-overlay"></div> 
      <div class="rent-modal"> 
        <button class="rent-modal-close" aria-label="Close">&times;</button> 
        <h2>BANANA FLOW</h2> 
        <div class="shopify-form-target"></div> 
      </div> 
    `;

    document.body.appendChild(modal); 

    // clone Shopify form 
      const template = document.querySelector('#shopifyContactFormTemplate'); 
      const form = template.querySelector('form').cloneNode(true); 

      // apply layout CSS 
      form.classList.add('rent-request-form'); 

        modal.querySelector('.shopify-form-target').appendChild(form); 

        // fill body 
        const bodyField = form.querySelector('.js-contact-body'); 
        bodyField.value = 
        `Mietanfrage 
        
        ${itemsText} 
            Mietdauer: ${rentDuration} Monate 
            Monatlich: ${this.formatMoney(totalRent)} 
            Bereitstellungskosten: ${this.formatMoney(additionalFee)} 
            `; 

        bodyField.style.display = 'none'; 

        // visual summary box with images and mini invoice-style totals 
        const itemsBox = document.createElement('div'); 
        itemsBox.className = 'rent-modal-items'; 
        itemsBox.innerHTML = `
          <h3>Übersicht deiner Anfrage</h3>
          <div class="rent-summary-grid">
            <div class="rent-items-list">
              <ul>
                ${Object.entries(groupedItems).map(([title, data]) => `
                  <li class="rent-item">
                    ${data.image ? `<img src="${data.image}" alt="${title}">` : ''}
                    <div>
                      <strong>${title}</strong><br>
                      ${data.color ? `Farbe: ${data.color}<br>` : ''}
                      Menge: ${data.qty}
                    </div>
                  </li>
                `).join('')}
                </ul> 
              </div> 
              <div class="rent-totals"> 
                <p><strong>Mietdauer:</strong> ${rentDuration} Monate</p> 
                <p><strong>Monatlich:</strong> ${this.formatMoney(totalRent)}</p> 
                <p><strong>Bereitstellungskosten:</strong> ${this.formatMoney(additionalFee)}</p> 
              </div> 
            </div> 
        `;
            form.prepend(itemsBox); 

          const extra = form.querySelector('.js-extra-fields'); 
    extra.innerHTML = `
      <div class="half-row">
        <label>
          Vorname *
          <input name="first_name" required>
        </label>

        <label>
          Nachname *
          <input name="last_name" required>
        </label>
      </div>

      <label class="full-width">
        E-Mail *
        <input type="email" name="contact[email]" required>
      </label>

      <label>
        Firmenname *
        <input name="contact[company]" required>
      </label>

      <label>
        Telefon
        <input name="contact[phone]">
      </label>

      <label class="full-width">
        Nachricht
        <textarea name="message" rows="4" style="height: 80px;"></textarea>
      </label>
    `;


    // info text 
    const info1 = document.createElement('p'); 
    info1.className = 'rent-info'; 
    info1.textContent = 'Dies ist eine unverbindliche Mietanfrage'; 

    form.appendChild(info1); 

    this.bindRequestModalEvents(modal, form); 
  }




  bindRequestModalEvents(modal, form) { 
    const overlay = modal.querySelector('.rent-modal-overlay'); 
    const closeBtn = modal.querySelector('.rent-modal-close'); 

    const closeModal = () => modal.remove(); 

    overlay.addEventListener('click', closeModal); 
    closeBtn.addEventListener('click', closeModal); 

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeModal();
    }, { once: true });

    // before submit combine names 
    form.addEventListener('submit', () => { 
      const first = form.querySelector('[name="first_name"]')?.value || ''; 
      const last = form.querySelector('[name="last_name"]')?.value || ''; 
      const message = form.querySelector('[name="message"]')?.value || ''; 

      let nameInput = form.querySelector('[name="contact[name]"]'); 
      if (!nameInput) {
        nameInput = document.createElement('input');
        nameInput.type = 'hidden';
        nameInput.name = 'contact[name]';
          form.appendChild(nameInput); 
        } 

      nameInput.value = `${first} ${last}`;

      // append customer message to body 
      const bodyField = form.querySelector('.js-contact-body'); 
      bodyField.value += `\n\nNachricht:\n${message}`;
    }); 
  }









  


  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

window.customElements.define('cart-drawer', CartDrawer);

document.addEventListener('DOMContentLoaded', () => {
  const params = new URLSearchParams(window.location.search);

  if (params.get('rent_success') === 'true') {
    showSuccessPopup();
    window.history.replaceState({}, document.title, window.location.pathname);
  }
});

function showSuccessPopup() {
  if (document.querySelector('.rent-success-overlay')) return;

  const overlay = document.createElement('div');
  overlay.className = 'rent-success-overlay';

  overlay.innerHTML = `
    <div class="rent-success-modal">
      <p class="rent-success-info">Deine Anfrage wurde gesendet!<br>
      Wir melden uns schnellstmöglich bei Dir.</p>
      <button class="rent-success-ok">OK</button>
    </div>
  `;

  document.body.appendChild(overlay);

  const close = () => {
    overlay.classList.remove('visible');
    setTimeout(() => overlay.remove(), 200);
  };

  requestAnimationFrame(() => {
    overlay.classList.add('visible');
  });

  overlay.querySelector('.rent-success-ok')
    .addEventListener('click', close);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  document.addEventListener('keydown', function escListener(e) {
    if (e.key === 'Escape') {
      close();
      document.removeEventListener('keydown', escListener);
    }
  });
}

